import React from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';
import getConfig from 'next/config';

import { RouteContext } from 'lib/ContextTypes';
import { stripTrailingSlash } from 'lib/urlUtils';
import { useHideMobileNavOnScroll } from 'lib/Hooks/useHideMobileNavOnScroll';

import styles from './styles.module.scss';

/**
 * Navbar is a functional component that renders a navigation bar with dynamic content
 * and customizable features. It supports global and local inserts, mobile navigation,
 * and theme-based styling.
 *
 * @param {object} props - The component props.
 * @param {string} props.html - The HTML content to be rendered inside the navigation bar.
 * @param {boolean} props.active - Indicates if the navigation bar is active.
 * @param {boolean} props.hasGlobalInsert - Specifies if a global insert is present.
 * @param {boolean} props.hasLocalInsert - Specifies if a local insert is present.
 * @param {boolean} props.shortcuts - Indicates if shortcuts are displayed.
 * @param {boolean} props.showGlobal - Determines if the global navigation is shown.
 * @param {boolean} props.showIdentity - Determines if the identity section is shown.
 * @param {string} props.theme - The theme to be applied to the navigation bar.
 * @param {boolean} [props.showPolitics=false] - Indicates if the politics section is shown.
 * @param {boolean} [props.useSmallNav=false] - Indicates if a smaller navigation bar is used.
 * @param {number|null} [props.localAnimationDuration=null] - The duration of local animations in seconds.
 */
function Navbar(props) {
  let {
    html,
  } = props;

  const {
    active,
    hasGlobalInsert,
    hasLocalInsert,
    mobileNavInsertVisibility,
    shortcuts,
    showGlobal,
    showIdentity,
    theme,
    showPolitics = false,
    useSmallNav = false,
    localAnimationDuration = null,
    vertical,
  } = props;

  const {
    publicRuntimeConfig: {
      CNBC_STAGE_DOMAIN,
      ENABLE_RELATIVE_LINKS,
    },
  } = getConfig();

  // Remove production hostname from links in navbar html
  if (ENABLE_RELATIVE_LINKS) {
    const route = React.useContext(RouteContext);
    // Production hostname
    const prodHostname = `https://www.${route.domain}`;

    // Find links to root hostname - non capture hostname without trailing slash
    const regexRoot = new RegExp(`(?:${prodHostname})(?!/)`, 'g');
    // Replace links to root first
    html = html.replace(regexRoot, '/');

    // Find all other hostname non-search links
    const regexSub = new RegExp(`(?:${prodHostname})(/(?!search|archive[/'"])[^'"]*)`, 'g');
    // Then replace hostname on sub links
    html = html.replace(regexSub, '$1');
  }

  // Replace CNBC URLs with configured stage domain
  if (CNBC_STAGE_DOMAIN) {
    const regexCnbc = new RegExp('https://www.cnbc.com', 'g');
    html = html.replace(regexCnbc, stripTrailingSlash(CNBC_STAGE_DOMAIN));
  }

  const [isScrolled, setIsScrolled] = React.useState(false);

  const customLocalDuration = typeof localAnimationDuration === 'number'
    && !Number.isNaN(localAnimationDuration)
    ? { '--hfs-local-duration': `${localAnimationDuration}s` }
    : null;


  React.useEffect(() => {
    if (window.scrollY) {
      setIsScrolled(true);
    }
  }, []);

  const {
    hideMobileNav,
    hideMobileNavInsert,
  } = useHideMobileNavOnScroll(vertical, { mobileNavInsertVisibility });

  return (
    <nav
      style={customLocalDuration}
      suppressHydrationWarning // content from HFS is mixed SSR and hydration, won't match ramen SSR
      className={classNames(
        'hfsh init',
        {
          active,
          scrolled: isScrolled,
          [styles.hideMobileNav]: hideMobileNav,
          'has-global-insert': hasGlobalInsert,
          'has-local-insert': hasLocalInsert,
          'has-mobilenav-insert': !hideMobileNavInsert,
          small: useSmallNav,
          'show-global': showGlobal,
          'hide-global': !showGlobal,
          'show-identity': showIdentity,
          'show-shortcuts': shortcuts,
          politics: showPolitics,
          [theme]: theme,
        },
        [styles.override],
      )}
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{
        __html: html,
      }}
    />
  );
}

const mapStateToProps = ({ navbar }) => ({
  active: navbar.active,
  hasGlobalInsert: navbar.hasGlobalInsert,
  hasLocalInsert: navbar.hasLocalInsert,
  shortcuts: navbar.shortcutsVisible,
  theme: navbar.theme,
  localAnimationDuration: navbar.localAnimationDuration,
});

export const HeaderNavbar = connect(mapStateToProps)(
  Navbar,
);
